import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { faqs, howItWorks, purchaseSeatsLanguages } from "./helpers";
import { routes } from "@/global/routes";
import { vendorUUIDs } from "../../constants";
import StaticSeatMap from "public/images/tulsadrillers_flex/TulsaDrillersMap.png";
import FindGamesBannerImage from "public/images/tulsadrillers_flex/2025-PickEm-1486x148 (002).png";
import type { TeamConfig } from "@/global/hooks/useTeamConfig";
import Link from "@mui/material/Link";

const config: TeamConfig = {
  teamSchema: "tulsadrillers_flex",
  browserTitle: "Pick'em Pack", // Updated
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "", // Optional, set to empty string
  teamName: "Tulsa Drillers",

  // Seat map configs
  seatMapConfigs: {
    venueId: "", // Not available
    mapId: "", // Not available
    staticSeatMapImage: StaticSeatMap.src, // Use the static seat map image
    isShowAvailableSeatsButtonHidden: true,
    isView3dButtonHidden: true,
    customSeatMapStaticImageStyle: {
      width: "70%",
      display: "block",
      marginX: "auto",
    },
  },

  // Gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: "",
    redeemSuccessModalLink: "",
    hideRedeemItem: false,
    customShareGiftUrl: "",
    customErrorMessage: "",
    hideRecipient: false,
  },

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        discount: 10,
        customProgressBarMessage:
          "Congrats! You've unlocked 10% off your seats! Add 2 more games for 20% off, 4 more games for 30% off or checkout now.",
      },
      {
        id: 2,
        minSelectedAmount: 3,
        updatedAccountGroup: 0,
        discount: 10,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Congrats! You've unlocked 10% off your seats! Add 1 more game for 20% off, 3 more games for 30% off or checkout now.",
      },
      {
        id: 3,
        minSelectedAmount: 4,
        updatedAccountGroup: 1,
        discount: 20,
        customProgressBarMessage:
          "Congrats! You have unlocked 20% off your seats! Add 2 more games for 30% off or checkout now.",
      },
      {
        id: 4,
        minSelectedAmount: 5,
        updatedAccountGroup: 1,
        discount: 20,
        hideOfferFromModal: true,
        customProgressBarMessage: "Add 1 more game for 30% off your seats.",
      },
      {
        id: 5,
        minSelectedAmount: 6,
        updatedAccountGroup: 2,
        discount: 30,
        customProgressBarMessage:
          "Congrats! You have unlocked 30% off your seats! Add more games or checkout now.",
      },
    ],
  },

  // Purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // Disabled routes
  disabledRoutes: [routes.giftCard],

  // Switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // Purchase seat configs
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 10,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    hideViewTicketsInThankYouPage: false,
    defaultSeatCount: 2, // Updated
    checkoutTimerEnabled: false,
    isDisabledEventSelection: false,
    hideAllUpcomingGames: false,
    isRepProduct: false,
  },

  // Feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // Intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // Checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity, // No limit specified
    allowedPaymentFailCount: Infinity, // No limit specified
  },

  isSaveCardAllowed: true,
  isBrainTreePaymentEnabled: false,
  shouldUseInternalLogin: false,
  oktaClientId: "0oasmcdgh92Jom7TI357",
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  platform: vendorUUIDs.tickesdotcom, // TDC platform
  refreshInventoryWhenEventClick: false,

  // Analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "",
  },

  // Redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // URLs
  accountManagerUrl:
    "https://mlb.tickets.com/?agency=TLDM_MYTIXX&orgid=57370&_wcsid=CB8671B60651CF3ABB0A1A901E469B8584DAE2E28BA19C3DD2A4146F44E9B90D#/auth/login?fullret=%2Fticketmanagement%2F%3Fagency%3DTLDM_MYTIXX%26orgid%3D57370",

  // Images
  images: {
    findGamesBannerImage: FindGamesBannerImage.src,
    authBackgroundImage: FindGamesBannerImage.src, // Using findGamesBannerImage
    checkoutStatusPageImage: FindGamesBannerImage.src,
    logoImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/tulsa-drillers-logo.png" +
      "?width=128&height=75", // actual size 500x134
    logoNameImage: "",
    faviconImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/tulsa-drillers-logo.png",
    seatMap3dPreviewImage: "",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },
    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },
    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },
    progressBar: {
      staticProgressBarText: false,
    },
    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:918-744-5901"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (918) 744-5901
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#005294",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: purchaseSeatsLanguages,
    // Gift card languages are empty since gift cards are disabled
    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,
    checkoutSuccessDescription: (
      <>
        Your Tulsa Drillers tickets are confirmed! Your tickets will be
        available within 24 hours in your{" "}
        <Link
          target="_blank"
          href="https://mlb.tickets.com/?agency=TLDM_MYTIXX&orgid=57370&_wcsid=CB8671B60651CF3ABB0A1A901E469B8584DAE2E28BA19C3DD2A4146F44E9B90D#/auth/login?fullret=%2Fticketmanagement%2F%3Fagency%3DTLDM_MYTIXX%26orgid%3D57370"
        >
          MyProVenue
        </Link>{" "}
        account.
      </>
    ),
    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
